<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Select from '@pages/Shared/form/Select.vue'
import Input from '@pages/Shared/form/Input.vue'
import { debounce } from 'lodash-es'
import { PhPrinter, PhArrowsMerge } from '@phosphor-icons/vue'

import { useResultStore } from '@/Stores/useResult'

import CombinedResultDrawer from './components/CombinedResultDrawer.vue'

const props = defineProps({
    sessions:          Array,
    terms:             Array,
    classes:           Array,
    results:           Array,
    activeSession:     Object,
    filters:           Object,
    assessmentColumns: Object,
    subjectList:       Array,
    sections:          Array,
})

const resultStore = useResultStore()

const searchForm = useForm({
    search:     props.filters?.search || '',
    term_id:    props.filters?.term_id || null,
    class_id:   props.filters?.class_id || null,
    session_id: props.filters?.session_id || null,
})

const isReady            = ref(false)
const displayResults     = ref(props.results ?? [])
const showCombinedDrawer = ref(false)

const page             = usePage()
const can              = computed(() => page.props?.can || {})

// ── Selected data ──────────────────────────────────────────────────────────

const selectedClass = computed(() =>
    props.classes?.find(c => String(c.id) === String(searchForm.class_id)) ?? null
)

const filteredTerms = computed(() => {
    if (!searchForm.session_id) return props.terms ?? []

    const session = props.sessions?.find(
        s => String(s.id) === String(searchForm.session_id)
    )

    if (session?.terms?.length) return session.terms

    return (props.terms ?? []).filter(
        t => String(t.academic_session_id) === String(searchForm.session_id)
    )
})

const selectedTerm = computed(() =>
    filteredTerms.value.find(
        t => String(t.id) === String(searchForm.term_id)
    ) ?? null
)

const selectedSession = computed(() =>
    props.sessions?.find(
        s => String(s.id) === String(searchForm.session_id)
    ) ?? null
)

const formMaster = computed(() =>
    selectedClass.value?.form_master?.name ?? 'N/A'
)

const subjectColumns = computed(() =>
    props.subjectList || []
)

const activeTermEndAt = computed(() =>
    selectedTerm.value?.ends_at ?? null
)

// ── Options ────────────────────────────────────────────────────────────────

const sessionOptions = computed(() =>
    (props.sessions ?? []).map(s => ({
        label: s.name,
        value: s.id,
    }))
)

const termOptions = computed(() =>
    filteredTerms.value.map(t => ({
        label: t.name,
        value: t.id,
    }))
)

const classOptions = computed(() =>
    (props.classes ?? []).map(c => ({
        label: c.name,
        value: c.id,
    }))
)

// ── Section info ───────────────────────────────────────────────────────────

const sectionInfo = computed(() => {
    if (!props.sections?.length || !selectedClass.value) {
        return {
            sectionName: '',
            nextClass: null,
        }
    }

    for (const section of props.sections) {
        const match = section.classes?.find(
            c => String(c.id) === String(selectedClass.value.id)
        )

        if (match) {
            return {
                sectionName: section.name ?? '',
                nextClass: match.next_class_room ?? null,
            }
        }
    }

    return {
        sectionName: '',
        nextClass: null,
    }
})

// ── Store sync ─────────────────────────────────────────────────────────────

const syncStore = () => {
    if (!displayResults.value?.length || !props.subjectList?.length) return

    const className = selectedClass.value?.name ?? ''
    const termName  = selectedTerm.value?.name ?? ''
    const yearName  = selectedSession.value?.name ?? ''

    const { sectionName, nextClass } = sectionInfo.value

    const mapped = displayResults.value.map(r => ({
        student: {
            name: [
                r.student.first_name,
                r.student.last_name,
                r.student.other_name,
            ]
                .filter(Boolean)
                .join(' '),

            id: r.student.id,
        },

        form_master:  formMaster.value,
        next_class:   nextClass,
        section_name: sectionName,

        subjects: props.subjectList.map(subject => {
            const assessments =
                r.subject_scores[subject.id]?.assessments || {}

            const assessmentList = Object.values(assessments)

            const ca =
                assessmentList.find(a =>
                    a.name?.toLowerCase().includes('ca')
                ) ?? assessmentList[0] ?? null

            const exam =
                assessmentList.find(a =>
                    a.name?.toLowerCase().includes('exam')
                ) ?? assessmentList[1] ?? null

            return {
                subject:    subject.name,
                arabic:     subject.arabic ?? '',
                ca_score:   ca?.score ?? null,
                exam_score: exam?.score ?? null,
            }
        }),

        total_score:   r.total_score,
        average_score: r.average,
        rank:          r.position_text,
    }))

    resultStore.setResults(
        { [className]: mapped },
        termName,
        yearName,
        activeTermEndAt.value
    )
}

// ── Requests ───────────────────────────────────────────────────────────────

const fetchResults = debounce(() => {
    if (
        !isReady.value ||
        !searchForm.session_id ||
        !searchForm.term_id
    ) return

    searchForm.get('/staff/manage-results', {
        preserveState:  true,
        preserveScroll: true,
        replace:        true,
        only: [
            'results',
            'filters',
            'assessmentColumns',
            'subjectList',
        ],
    })
}, 250)

const fetchResultsImmediate = () => {
    fetchResults.cancel()

    if (
        !isReady.value ||
        !searchForm.session_id ||
        !searchForm.term_id
    ) return

    searchForm.get('/staff/manage-results', {
        preserveState:  true,
        preserveScroll: true,
        replace:        true,
        only: [
            'results',
            'filters',
            'assessmentColumns',
            'subjectList',
        ],
    })
}

// ── Lifecycle ──────────────────────────────────────────────────────────────

onMounted(() => {
    const session = props.activeSession

    searchForm.session_id ||= session?.id
    searchForm.class_id ||= props.classes?.[0]?.id

    if (!searchForm.term_id) {
        const sessionTerms = session?.terms ?? filteredTerms.value

        const activeTerm = sessionTerms.find(t => t.is_active)

        searchForm.term_id =
            activeTerm?.id ??
            sessionTerms[0]?.id ??
            null
    }

    isReady.value = true
})

// ── Watchers ───────────────────────────────────────────────────────────────

watch(() => props.results, (val) => {
    displayResults.value = val ?? []
    syncStore()
})

watch(() => searchForm.session_id, (newVal, oldVal) => {
    if (!oldVal || newVal === oldVal) return

    const session = props.sessions?.find(
        s => String(s.id) === String(newVal)
    )

    const terms = session?.terms ?? []

    const active = terms.find(t => t.is_active)

    searchForm.term_id =
        active?.id ??
        terms[0]?.id ??
        null
})

watch(() => [
    searchForm.class_id,
    searchForm.term_id,
    searchForm.session_id,
], () => {
    if (!isReady.value) return
    fetchResultsImmediate()
})

watch(() => searchForm.search, () => {
    if (!isReady.value) return
    fetchResults()
})

// ── Print handlers ─────────────────────────────────────────────────────────

const handlePrintStudent = (admissionNumber, studentName) =>
    resultStore.generateResult(
        selectedClass.value?.name ?? '',
        {
            id: admissionNumber,
            name: studentName,
        }
    )

const handlePrintAll = () =>
    resultStore.generateResult(
        selectedClass.value?.name ?? ''
    )

const handlePrintClassReport = () =>
    resultStore.generateResultSheet(
        selectedClass.value?.name ?? ''
    )

// ── UI helpers ─────────────────────────────────────────────────────────────

const performanceClass = (avg) => {
    if (avg === null || avg === undefined) return 'text-gray-400'
    if (avg >= 70) return 'text-green-600'
    if (avg >= 50) return 'text-amber-500'
    return 'text-red-500'
}
</script>

<template>
    <Layout>
        <Head title="Student Results" />

        <div class="space-y-6">

            <!-- Header -->
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                <div>
                    <h2 class="text-xl font-bold text-primary">
                        {{ selectedClass?.name ?? 'Student Results' }}
                    </h2>

                    <p class="text-sm text-gray-500 mt-0.5">

                        <span v-if="selectedTerm">
                            {{ selectedTerm.name }}
                        </span>

                        <template v-if="selectedSession?.name">
                            &nbsp;·&nbsp;{{ selectedSession.name }}
                        </template>

                        <template v-if="selectedClass?.form_master_name">
                            &nbsp;·&nbsp;Form Master:
                            <span class="font-medium text-gray-700">
                                {{ formMaster }}
                            </span>
                        </template>
                    </p>
                </div>

                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">

                    <button
                        v-if="can.combineResult"
                        @click="showCombinedDrawer = true"
                        class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                               text-primary bg-primary-50 hover:bg-primary-100 border border-primary-200
                               rounded-lg transition"
                    >
                        <PhArrowsMerge :size="16" />
                        Combined Report
                    </button>

                    <button
                        v-if="can.printResult"
                        @click="handlePrintAll"
                        :disabled="!displayResults.length || resultStore.isGenerating"
                        class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-white
                               bg-primary hover:bg-primary-light rounded-lg transition
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PhPrinter :size="16" />

                        {{ resultStore.isGenerating ? 'Generating…' : 'Print All' }}
                    </button>

                    <button
                        @click="handlePrintClassReport"
                        :disabled="!displayResults.length || resultStore.isGenerating"
                        class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-white
                               bg-edit hover:bg-edit-light rounded-lg transition
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PhPrinter :size="16" />

                        {{ resultStore.isGenerating ? 'Generating…' : 'Class Report' }}
                    </button>
                </div>
            </div>

            <!-- Table -->
            <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

                <!-- Filters -->
                <div class="flex flex-col gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50
                            sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">

                    <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap">

                        <Select
                            v-model="searchForm.session_id"
                            :options="sessionOptions"
                            placeholder="Select session"
                            class="w-full sm:w-44"
                        />

                        <Select
                            v-model="searchForm.term_id"
                            :options="termOptions"
                            placeholder="Select term"
                            :disabled="!searchForm.session_id"
                            class="w-full sm:w-40"
                        />

                        <Select
                            v-model="searchForm.class_id"
                            :options="classOptions"
                            placeholder="Select class"
                            class="w-full sm:w-40"
                        />
                    </div>

                    <Input
                        v-model="searchForm.search"
                        placeholder="Search student..."
                        class="w-full sm:w-56"
                    />
                </div>

                <!-- Table -->
                <div
                    class="overflow-x-auto"
                    :class="{ 'opacity-60 cursor-wait': searchForm.processing || resultStore.isGenerating }"
                >

                    <table class="w-full text-sm border-collapse min-w-max">

                        <thead>

                            <tr class="bg-gray-50 border-b border-gray-200">

                                <th
                                    rowspan="2"
                                    class="px-4 py-3 sm:sticky sm:left-0 bg-gray-50 sm:z-20 border-r
                                           text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[240px]"
                                >
                                    Student
                                </th>

                                <template v-for="s in subjectColumns" :key="s.id">

                                    <th
                                        :colspan="assessmentColumns[s.id]?.length || 1"
                                        class="px-2 py-3 text-center text-xs font-semibold text-gray-700
                                               uppercase tracking-wider border-x border-gray-100"
                                    >
                                        {{ s.name }}
                                    </th>
                                </template>

                                <th
                                    colspan="3"
                                    class="px-4 py-3 text-center text-xs font-semibold text-gray-600
                                           uppercase tracking-wider border-l border-gray-100"
                                >
                                    Summary
                                </th>

                                <th rowspan="2" class="px-4 py-3"></th>
                            </tr>

                            <tr class="bg-gray-100 border-b border-gray-200">

                                <template v-for="s in subjectColumns" :key="s.id">

                                    <th
                                        v-for="a in assessmentColumns[s.id]"
                                        :key="a.id"
                                        class="px-2 py-2 text-center text-xs font-medium text-gray-600"
                                    >
                                        {{ a.name }}
                                    </th>

                                    <th
                                        v-if="!assessmentColumns[s.id]?.length"
                                        class="px-2 py-2 text-center text-gray-400"
                                    >
                                        —
                                    </th>
                                </template>

                                <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                                    Total
                                </th>

                                <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                                    Average
                                </th>

                                <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                                    Position
                                </th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-100">

                            <tr
                                v-for="r in displayResults"
                                :key="r.student.id"
                                :class="[
                                    'transition-colors',
                                    r.position === 1 ? 'bg-green-50'
                                  : r.position === 2 ? 'bg-amber-50'
                                  : r.position === 3 ? 'bg-orange-50'
                                  : 'hover:bg-gray-50'
                                ]"
                            >

                                <td class="px-4 py-3 sm:sticky sm:left-0 sm:z-10 bg-white border-r whitespace-nowrap">

                                    <div class="flex flex-col">

                                        <span class="text-sm font-medium text-gray-800">
                                            {{ r.student.first_name }} {{ r.student.last_name }} {{ r.student.other_name ? r.student.other_name : '' }}
                                        </span>

                                        <span class="text-xs text-gray-600 uppercase tracking-wider">
                                            {{ r.student.id }}
                                        </span>
                                    </div>
                                </td>

                                <template v-for="s in subjectColumns" :key="s.id">

                                    <td
                                        v-for="a in assessmentColumns[s.id]"
                                        :key="a.id"
                                        class="px-2 py-3 text-center text-sm text-gray-600"
                                    >
                                        {{ r.subject_scores[s.id]?.assessments[a.id]?.score ?? '—' }}
                                    </td>

                                    <td
                                        v-if="!assessmentColumns[s.id]?.length"
                                        class="px-2 py-3 text-center text-gray-400"
                                    >
                                        —
                                    </td>
                                </template>

                                <td class="px-3 py-3 text-center">

                                    <span class="inline-flex items-center px-2 py-0.5 text-xs font-semibold
                                                 rounded-full bg-primary-50 text-primary border border-primary-100">
                                        {{ r.total_score }}
                                    </span>
                                </td>

                                <td class="px-3 py-3 text-center">

                                    <span
                                        class="text-sm font-semibold"
                                        :class="performanceClass(r.average)"
                                    >
                                        {{ r.average }}
                                    </span>
                                </td>

                                <td class="px-3 py-3 text-center">

                                    <span
                                        class="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border"
                                        :class="
                                            r.position === 1 ? 'bg-green-50 text-green-700 border-green-100'
                                          : r.position === 2 ? 'bg-amber-50 text-amber-700 border-amber-100'
                                          : r.position === 3 ? 'bg-orange-50 text-orange-700 border-orange-100'
                                          : 'bg-gray-50 text-gray-600 border-gray-200'
                                        "
                                    >
                                        {{ r.position_text }}
                                    </span>
                                </td>

                                <td class="px-4 py-3 text-center">

                                    <button
                                        v-if="can.printResult"
                                        @click="handlePrintStudent(
                                            r.student.id,
                                            `${r.student.first_name}${r.student.other_name ? ' ' + r.student.other_name : ''} ${r.student.last_name}`
                                        )"
                                        :disabled="resultStore.isGenerating"
                                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                                               text-primary bg-primary-50 hover:bg-primary-100 rounded-md border border-primary-100
                                               transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <PhPrinter :size="13" />
                                        Print
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty -->
                <div
                    v-if="!displayResults.length"
                    class="flex h-48 flex-col items-center justify-center text-center px-6"
                >
                    <p class="text-sm text-gray-400">
                        No results found for this class and term.
                    </p>

                    <p class="text-xs text-gray-300 mt-1">
                        Try adjusting the filters or search input.
                    </p>
                </div>
            </div>
        </div>

        <CombinedResultDrawer
            :open="showCombinedDrawer"
            :classes="classes"
            :terms="terms"
            :sessions="sessions"
            :term-id="searchForm.term_id"
            :session-id="searchForm.session_id"
            @close="showCombinedDrawer = false"
        />
    </Layout>
</template>